<template>
    <div class="grid-container">
        <div class="grid-header" @click="collapsed = !collapsed">
            <div class="grid-title">
                <span class="report-name">
                    <span class="collapse-chevron" :class="{ 'is-collapsed': collapsed }">▾</span>
                    {{ section.icon }} {{ section.label }} Report
                    <span class="record-count-badge" :class="section.statusBadgeClass">
                        {{ (section.report && section.report.status) || 'Automated' }}
                    </span>
                </span>
                <span class="date-range-label" v-if="section.report">
                    {{ section.report.accountName }} · {{ formatWindow(section.report.windowStart) }} → {{ formatWindow(section.report.windowEnd) }}
                </span>
            </div>
            <div class="grid-controls" v-if="section.report && section.report.pdfBlobName && section.report.status !== 'Failed'">
                <a :href="section.report.comment" target="_blank" rel="noopener" class="action-button export-btn" @click.stop>
                    <span>📄</span><span>Download PDF</span>
                </a>
            </div>
        </div>

        <div class="report-body" v-show="!collapsed">
            <div v-if="section.report && section.report.status === 'Failed'" class="failure-banner">
                <strong>Report generation failed:</strong> {{ section.report.comment }}
            </div>

            <!-- KPI cards -->
            <div class="stats-container" v-if="section.kpiCards.length">
                <div class="stat-card" v-for="kpi in section.kpiCards" :key="kpi.label"
                    :style="{ borderLeftColor: kpi.color }">
                    <div class="stat-label">{{ kpi.label }}</div>
                    <div class="stat-value" :style="{ color: kpi.color }">{{ kpi.value }}</div>
                    <div class="stat-change" v-if="kpi.hint">{{ kpi.hint }}</div>
                </div>
            </div>

            <!-- Simple count/volume summary (Transfer/Receiving) -->
            <div class="stats-container" v-if="section.isSimpleCategory">
                <div class="stat-card" style="border-left-color:#E8720C;">
                    <div class="stat-label">Record Count</div>
                    <div class="stat-value">{{ section.detail.count != null ? section.detail.count : 0 }}</div>
                </div>
                <div class="stat-card" style="border-left-color:#1F2A44;">
                    <div class="stat-label">Total Volume (L)</div>
                    <div class="stat-value">{{ formatNum(section.detail.total_volume_l) }}</div>
                </div>
            </div>

            <!-- Management items -->
            <div v-if="section.managementItems.length" class="sub-section">
                <h4 class="sub-title">🔎 Items For Management (Investigate)</h4>
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th>Affected</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, i) in section.managementItems" :key="'m' + i" class="row-clickable"
                            @click="$emit('row-click', 'management', item)">
                            <td>{{ item.category }}</td>
                            <td>{{ item.affected }}</td>
                            <td class="note-cell">{{ item.note }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- ATG telemetry noise -->
            <div v-if="section.monitoringAtg.length" class="sub-section">
                <h4 class="sub-title">📡 ATG Telemetry Noise</h4>
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Tank</th>
                            <th>Window</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(m, i) in section.monitoringAtg" :key="'a' + i" class="row-clickable"
                            @click="$emit('row-click', 'atgNoise', m)">
                            <td>{{ m.tank }}</td>
                            <td>{{ m.window_start }} → {{ m.window_end }}</td>
                        </tr>
                    </tbody>
                </table>
                <p class="footnote">Rolling-stdev telemetry noise flags, not confirmed events. No action
                    required unless also flagged under possible fuel loss.</p>
            </div>

            <!-- Comm errors explained by near-empty tanks -->
            <div v-if="section.monitoringComm.length" class="sub-section">
                <h4 class="sub-title">📶 Communication Errors Explained By Near-Empty Tanks</h4>
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Device</th>
                            <th>Errors</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(m, i) in section.monitoringComm" :key="'c' + i" class="row-clickable"
                            @click="$emit('row-click', 'comm', m)">
                            <td>{{ m.device_id }}</td>
                            <td>{{ m.errors }}</td>
                            <td class="note-cell">{{ m.note }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Transactions (Usage detail) -->
            <div v-if="section.transactions.length" class="sub-section">
                <h4 class="sub-title">🧾 Transaction Detail</h4>
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Transaction ID</th>
                            <th>Equipment</th>
                            <th>Volume (L)</th>
                            <th>Classification</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(t, i) in section.transactions" :key="'t' + i" class="row-clickable"
                            @click="$emit('row-click', 'transaction', t)">
                            <td><code class="id-code">{{ t.transaction_id }}</code></td>
                            <td>{{ t.equipment_id }}</td>
                            <td>{{ formatNum(t.volume) }}</td>
                            <td>
                                <span class="status-pill" :class="classificationClass(t.classification)">
                                    {{ t.classification }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p class="footnote">Click a row to see why it was flagged.</p>
            </div>

            <div v-if="section.noFindings" class="no-findings-banner">
                ✅ No issues found for this account/date/category. Fully reconciled or within tolerance.
            </div>

            <div class="report-meta-footer" v-if="section.report">
                <span>Generated: {{ formatWindow(section.report.generatedAt) }}</span>
                <span v-if="section.report.reconciliationPct != null">
                    Reconciliation: {{ formatPct(section.report.reconciliationPct) }}
                </span>
                <span>Group: {{ section.report.groupName }}</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'IOTReportSection',

    props: {
        section: { type: Object, required: true }
    },

    data: function () {
        return {
            collapsed: false
        };
    },

    methods: {
        classificationClass: function (classification) {
            if (!classification) return '';
            if (classification.indexOf('Unexplained') === 0) return 'pill-investigate';
            if (classification === 'Volume mismatch') return 'pill-investigate';
            return 'pill-monitor';
        },

        formatNum: function (v) {
            if (v == null) return 'n/a';
            var n = parseFloat(v);
            return isNaN(n) ? v : n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        formatPct: function (v) {
            if (v == null) return 'n/a';
            var n = parseFloat(v);
            return isNaN(n) ? v : n.toFixed(1) + '%';
        },

        formatWindow: function (v) {
            if (!v) return '—';
            var d = new Date(v);
            if (isNaN(d.getTime())) return v;
            var pad = function (n) { return String(n).padStart(2, '0'); };
            return d.getFullYear() + '/' + pad(d.getMonth() + 1) + '/' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
        }
    }
};
</script>

<style scoped>
.grid-container {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(31, 42, 68, .08);
    overflow: hidden;
    border: 1px solid #e5e1d8;
}

.grid-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    cursor: pointer;
    user-select: none;
}

.grid-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.report-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: #fff;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.collapse-chevron {
    display: inline-block;
    transition: transform .15s ease;
    font-size: .85em;
}

.collapse-chevron.is-collapsed {
    transform: rotate(-90deg);
}

.date-range-label {
    font-size: .85rem;
    color: #cfcfcf;
}

.record-count-badge {
    padding: 3px 12px;
    border-radius: 20px;
    font-size: .7rem;
    font-weight: 700;
    margin-left: 10px;
    text-transform: uppercase;
}

.badge-ok {
    background: #1F7A43;
    color: white;
}

.badge-failed {
    background: #C0392B;
    color: white;
}

.action-button {
    padding: 8px 16px;
    background: #E8720C;
    border: none;
    border-radius: 6px;
    color: white;
    font-size: .875rem;
    cursor: pointer;
    transition: all .2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    text-decoration: none;
}

.action-button:hover {
    background: #cf6409;
    transform: translateY(-1px);
}

.report-body {
    padding: 24px;
}

.failure-banner {
    background: #FBE4E1;
    border-left: 4px solid #C0392B;
    color: #C0392B;
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    font-size: .9rem;
}

.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.stat-card {
    background: #F4F1EC;
    border-radius: 10px;
    padding: 16px;
    border-left: 4px solid #E8720C;
}

.stat-label {
    color: #E8720C;
    font-size: .78rem;
    margin-bottom: 6px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .3px;
}

.stat-value {
    font-size: 1.6rem;
    color: #1F2A44;
    font-weight: 700;
}

.stat-change {
    font-size: .75rem;
    margin-top: 4px;
    color: #8a8478;
}

.sub-section {
    margin-bottom: 24px;
}

.sub-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1F2A44;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 2px solid #E8720C;
    display: inline-block;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e5e1d8;
    border-radius: 6px;
    overflow: hidden;
}

.data-table thead {
    background: #2B2B2B;
}

.data-table th {
    padding: 10px 14px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: .8rem;
    text-transform: uppercase;
    letter-spacing: .4px;
}

.data-table td {
    padding: 10px 14px;
    font-size: .875rem;
    color: #2B2B2B;
    border-bottom: 1px solid #eee8db;
}

.note-cell {
    max-width: 360px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.data-table tbody tr:nth-child(even) {
    background: #FAF8F3;
}

.data-table tbody tr:hover {
    background: #FDF3E9;
}

.row-clickable {
    cursor: pointer;
}

.id-code {
    background: #F4F1EC;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: .8rem;
}

.footnote {
    font-size: .78rem;
    color: #8a8478;
    font-style: italic;
    margin-top: 8px;
}

.status-pill {
    padding: 2px 10px;
    border-radius: 20px;
    font-size: .75rem;
    font-weight: 700;
}

.pill-investigate {
    background: #FBE4E1;
    color: #C0392B;
}

.pill-monitor {
    background: #FBEBC9;
    color: #8A5A00;
}

.no-findings-banner {
    background: #E3F3E8;
    color: #1F7A43;
    border-left: 4px solid #1F7A43;
    padding: 14px 18px;
    border-radius: 6px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 20px;
}

.report-meta-footer {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    padding-top: 16px;
    border-top: 1px solid #eee8db;
    font-size: .8rem;
    color: #8a8478;
}
</style>
